import { isDevBuild, devInfo } from "./devWarn";

/** Anything the registry can evict — `HallidayPayments` satisfies this through its `destroy()`. */
export type RegistrableInstance = { destroy: () => void };

/**
 * The dev-mode registry lives on `globalThis` (a `Symbol.for` slot) so it survives the bundler's HMR
 * module re-evaluation, which `import.meta.hot`/`module.hot` cannot reach inside `node_modules`.
 */
const REGISTRY_SYMBOL = Symbol.for("halliday.payments.instances");

/**
 * Resolve the registry — development builds + a real global only. Returns `null` in production (modules
 * evaluate once, so there are no ghosts) or during SSR, making every operation an inert no-op.
 */
const getRegistry = (): Map<string, RegistrableInstance> | null => {
    if (!isDevBuild()) return null;
    if (typeof globalThis === "undefined") return null;
    const holder = globalThis as Record<symbol, unknown>;
    let registry = holder[REGISTRY_SYMBOL] as Map<string, RegistrableInstance> | undefined;
    if (!registry) {
        registry = new Map<string, RegistrableInstance>();
        holder[REGISTRY_SYMBOL] = registry;
    }
    return registry;
};

/**
 * Register `instance` under `key`, evicting any live same-key instance via an ordinary `destroy()`.
 *
 * Callers invoke this **lazily** — on the instance's first DOM-touching action (`preload`/`open*`, through
 * the DOM-ready gate), never in the constructor — so a constructed-but-never-opened instance never joins
 * the registry and never evicts anyone (this is what keeps the React Provider safe under StrictMode).
 * Eviction is a plain `destroy()` (teardown runs with the DOM ready since we are already past the gate),
 * announced once via `console.info`. No-op in production / SSR.
 */
export const registerInstance = (key: string, instance: RegistrableInstance): void => {
    const registry = getRegistry();
    if (!registry) return;
    const existing = registry.get(key);
    if (existing && existing !== instance) {
        devInfo(
            `[HallidayPayments] Replaced instance '${key}' — expected after a hot reload; ` +
                "if you intended two concurrent instances, give each a distinct `instanceId`.",
        );
        existing.destroy(); // ordinary destroy — deregisters itself (below) and rebuilds cleanly on next preload()
    }
    registry.set(key, instance);
};

/** Deregister `instance` from `key` (only when it is still the registered holder). No-op in production / SSR. */
export const deregisterInstance = (key: string, instance: RegistrableInstance): void => {
    const registry = getRegistry();
    if (!registry) return;
    if (registry.get(key) === instance) registry.delete(key);
};
